import React, { useState } from 'react';
import { Send, X } from 'lucide-react';
import { BroadcastMessage, AdminRole } from '../types';
import { Badge, Button, Input } from '../../components/core';

interface BroadcastCenterProps {
  broadcasts: BroadcastMessage[];
  currentRole: AdminRole;
  onSendBroadcast: (msg: BroadcastMessage) => void;
  addToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

export const BroadcastCenter: React.FC<BroadcastCenterProps> = ({
  broadcasts,
  currentRole,
  onSendBroadcast,
  addToast,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isAuditor = currentRole === 'Auditor';

  const [formData, setFormData] = useState({
    title: '',
    category: 'Class Announcement',
    targetAudience: 'All Parents & Students',
    channels: ['In-App', 'WhatsApp'],
    messageBody: '',
  });

  const handleToggleChannel = (ch: string) => {
    if (formData.channels.includes(ch)) {
      setFormData({
        ...formData,
        channels: formData.channels.filter((c) => c !== ch),
      });
    } else {
      setFormData({
        ...formData,
        channels: [...formData.channels, ch],
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isAuditor) return;

    const newBroadcast: BroadcastMessage = {
      id: `BRD-${Math.floor(300 + Math.random() * 700)}`,
      ...formData,
      dispatchedTime: new Date().toLocaleString(),
      deliveryStatus: 'Delivered (145/145)',
    };

    onSendBroadcast(newBroadcast);
    addToast('success', 'Broadcast Dispatched', `Sent broadcast message via ${formData.channels.join(', ')}`);
    setIsModalOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="admin-card flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
        <h1 className="text-xl font-bold tracking-tight text-[var(--gv-ink)]">
          Broadcast Communication Center
        </h1>

        {!isAuditor && (
          <Button
            leftIcon={Send}
            onClick={() => {
              setFormData({
                title: '',
                category: 'Class Announcement',
                targetAudience: 'All Parents & Students',
                channels: ['In-App', 'WhatsApp'],
                messageBody: '',
              });
              setIsModalOpen(true);
            }}
          >
            Dispatch Broadcast Notice
          </Button>
        )}
      </div>

      <div className="admin-card overflow-hidden">
        <div className="border-b border-[var(--gv-border)] bg-stone-50/50 p-4">
          <h3 className="text-sm font-bold text-[var(--gv-ink)]">Broadcast Announcements History</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-[var(--gv-ink-muted)]">
            <thead className="border-b border-[var(--gv-border)] bg-stone-50 font-mono text-[10px] uppercase tracking-wider text-[var(--gv-ink-muted)]">
              <tr>
                <th className="px-4 py-2.5 font-bold">Broadcast ID</th>
                <th className="px-4 py-2.5 font-bold">Notice Title & Category</th>
                <th className="px-4 py-2.5 font-bold">Target Audience</th>
                <th className="px-4 py-2.5 font-bold">Channels</th>
                <th className="px-4 py-2.5 font-mono font-bold">Dispatched Time</th>
                <th className="px-4 py-2.5 font-bold">Delivery Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 bg-white font-medium">
              {broadcasts.map((brd) => (
                <tr key={brd.id} className="transition-colors hover:bg-stone-50/80">
                  <td className="px-4 py-3 font-mono font-bold text-[var(--gv-brand)]">{brd.id}</td>
                  <td className="px-4 py-3">
                    <div className="font-bold text-[var(--gv-ink)]">{brd.title}</div>
                    <div className="font-mono text-[10px] text-[var(--gv-ink-muted)]">{brd.category}</div>
                    <div className="mt-0.5 line-clamp-1 text-[11px] italic text-[var(--gv-ink-muted)]">
                      &ldquo;{brd.messageBody}&rdquo;
                    </div>
                  </td>
                  <td className="px-4 py-3 font-semibold text-[var(--gv-ink)]">{brd.targetAudience}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {brd.channels.map((ch, i) => (
                        <Badge key={i} variant="accent">
                          {ch}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-[var(--gv-ink-muted)]">{brd.dispatchedTime}</td>
                  <td className="px-4 py-3">
                    <Badge variant="success">{brd.deliveryStatus}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
          <div className="admin-card w-full max-w-lg space-y-4 p-6 text-[var(--gv-ink)] shadow-xl">
            <div className="flex items-center justify-between border-b border-[var(--gv-border)] pb-3">
              <h3 className="text-base font-bold">Dispatch Multi-Channel Broadcast</h3>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setIsModalOpen(false)}
              />
            </div>

            <form onSubmit={handleSubmit} className="space-y-3 text-xs">
              <Input
                label="Notice Title"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Special Paryushan Meeting Announcement"
              />

              <div className="grid grid-cols-2 gap-3">
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Notice Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3.5 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    <option value="Class Announcement">Class Announcement</option>
                    <option value="Niyam Reminder">Niyam Reminder</option>
                    <option value="Urgent Circular">Urgent Circular</option>
                  </select>
                </div>
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Target Audience</label>
                  <select
                    value={formData.targetAudience}
                    onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3.5 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    <option value="All Parents & Students">All Parents & Students</option>
                    <option value="Level 1 Students">Level 1 Students</option>
                    <option value="Level 2 Students">Level 2 Students</option>
                    <option value="Level 3 Students">Level 3 Students</option>
                    <option value="Teachers Only">Teachers Only</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-[var(--gv-ink-muted)]">
                  Delivery Channels
                </label>
                <div className="flex gap-4 rounded-lg border border-[var(--gv-border)] bg-stone-50 p-3">
                  {['In-App', 'WhatsApp', 'SMS'].map((ch) => (
                    <label key={ch} className="flex cursor-pointer items-center gap-2 font-semibold text-[var(--gv-ink)]">
                      <input
                        type="checkbox"
                        checked={formData.channels.includes(ch)}
                        onChange={() => handleToggleChannel(ch)}
                        className="rounded border-[var(--gv-border)] text-[var(--gv-brand)] focus:ring-[var(--gv-brand)]"
                      />
                      <span>{ch}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="w-full space-y-1.5">
                <label className="block text-xs font-bold text-[var(--gv-ink)]">Message Body</label>
                <textarea
                  rows={4}
                  required
                  value={formData.messageBody}
                  onChange={(e) => setFormData({ ...formData, messageBody: e.target.value })}
                  placeholder="Type announcement message text..."
                  className="admin-input w-full rounded-xl border border-[var(--gv-border)] bg-white p-3 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" leftIcon={Send}>
                  Dispatch Broadcast
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
